import React, { useState, useEffect } from 'react';
import { db, auth } from '../../services/firebaseConnection';
import { doc, getDoc, updateDoc, deleteDoc, deleteField, collection, getDocs, query, where, orderBy, FieldPath } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import LogoBarberia from '../../assets/LogoBarbearia.png';
import { useNavigate } from 'react-router-dom';
import { functions } from '../../services/firebaseConnection';
import { httpsCallable } from 'firebase/functions';
import './agendamento.css';
export default function Agendamentos() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState({});
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [businessHours, setBusinessHours] = useState({});

  // Limpa duplicidades APENAS no documento do próprio usuário logado.
  // (Antes varria e reescrevia a coleção inteira a cada visita de qualquer cliente,
  // o que deixava o site lento para todos.)
  const cleanupDuplicateAppointments = async (userId) => {
    if (!userId) return;
    try {
      const userDocRef = doc(db, "agendamentos", userId);
      const userSnap = await getDoc(userDocRef);
      if (!userSnap.exists()) return;

      const userData = userSnap.data();
      const dates = Object.keys(userData);
      let hasChanges = false;

      // Avalia cada data separadamente
      dates.forEach(date => {
        const appointmentsOnDate = userData[date];
        if (!Array.isArray(appointmentsOnDate)) return;

        const slotSet = new Set(); // chave: `${date}|${professional}|${hora}`
        const txIdSet = new Set();
        const endToEndSet = new Set();
        const sanitized = [];

        appointmentsOnDate.forEach(appointment => {
          const slotKey = `${date}|${appointment.professional}|${appointment.hora}`;
          const txKey = appointment.txid ? `${date}|${appointment.txid}` : null;
          const endToEndKey = appointment.endToEndId ? `${date}|${appointment.endToEndId}` : null;

          const isDuplicateSlot = slotSet.has(slotKey);
          const isDuplicateTx = txKey && txIdSet.has(txKey);
          const isDuplicateEndToEnd = endToEndKey && endToEndSet.has(endToEndKey);

          if (isDuplicateSlot || isDuplicateTx || isDuplicateEndToEnd) {
            hasChanges = true; // Identificou e removeu uma duplicidade
            return; // Ignora este agendamento para remover a duplicação antiga
          }

          // Marca combinações únicas como já vistas
          slotSet.add(slotKey);
          if (txKey) txIdSet.add(txKey);
          if (endToEndKey) endToEndSet.add(endToEndKey);

          sanitized.push(appointment);
        });

        // Substitui a lista apenas se houve mudanças
        if (hasChanges) {
          if (sanitized.length > 0) {
            userData[date] = sanitized;
          } else {
            userData[date] = deleteField();
          }
        }
      });

      if (hasChanges) {
        await updateDoc(userDocRef, userData);
      }
    } catch (error) {
      console.error("Erro ao limpar agendamentos duplicados:", error);
    }
  };

  // Verifica pagamentos pendentes APENAS do próprio usuário logado.
  // Se não houver pendência, não chama nenhuma Cloud Function (evita a demora
  // de inicialização do servidor em toda visita).
  const verifyPaymentsForPendingAppointments = async (userId) => {
    if (!userId) return;
    try {
      const pendingAppointments = await getPendingAppointments(userId);
      if (pendingAppointments.length === 0) {
        return; // Nada pendente: nada a verificar
      }
      const createPayment = httpsCallable(functions, 'createPixPayment');
      const paymentResponse = await createPayment({});
      const token = paymentResponse.data.access_token;
      const checkPixPayment = httpsCallable(functions, 'checkPixPayment');
      const checkPixPaymentResponse = await checkPixPayment({ token });
      const pixReceived = checkPixPaymentResponse.data.pix;
      // Se a lista estiver perto do teto de página da API (~100), pode estar
      // truncada — nesse caso só confirmamos, nunca excluímos por ausência
      const pixLooksComplete = Array.isArray(pixReceived) && pixReceived.length < 100;
      for (const appointment of pendingAppointments) {
        const txidMatch = pixReceived.find(pix => pix.txid === appointment.txid);
        if (txidMatch) {
          await updateAppointmentStatus(appointment.userId, appointment.date, appointment.txid, 'Confirmado', txidMatch);
        } else {
          // Excluir apenas pendências antigas (>30 min) — nunca uma recém-criada
          // cujo pagamento ainda pode estar em andamento
          const createdAtMs = appointment.createdAt ? Date.parse(appointment.createdAt) : NaN;
          const isOld = Number.isNaN(createdAtMs) ? true : (Date.now() - createdAtMs > 30 * 60 * 1000);
          if (isOld && pixLooksComplete) {
            await deleteAppointment(appointment.userId, appointment.date, appointment.txid);
          }
        }
      }
    } catch (error) {
      console.error("Erro ao verificar o pagamento: ", error);
    }
  };
  const getPendingAppointments = async (userId) => {
    const userSnap = await getDoc(doc(db, "agendamentos", userId));
    const pendingAppointments = [];
    if (!userSnap.exists()) return pendingAppointments;
    const userAppointments = userSnap.data();
    for (const date in userAppointments) {
      const appointmentsByDate = userAppointments[date];
      if (!Array.isArray(appointmentsByDate)) continue;
      for (const appointment of appointmentsByDate) {
        if (appointment.paymentStatus === "Pendente") {
          pendingAppointments.push({ ...appointment, userId, date });
        }
      }
    }
    return pendingAppointments;
  };
  const updateAppointmentStatus = async (userId, appointmentDate, appointmentTxid, status, paymentInfo) => {
    const userAppointmentsPath = `agendamentos/${userId}`;
    const appointmentRef = doc(db, userAppointmentsPath);
    const docSnap = await getDoc(appointmentRef);
    if (docSnap.exists()) {
      const userData = docSnap.data();
      const appointmentsOnDate = userData[appointmentDate] || [];
      const appointmentIndex = appointmentsOnDate.findIndex(appointment => appointment.txid === appointmentTxid);
      if (appointmentIndex !== -1) {
        appointmentsOnDate[appointmentIndex].paymentStatus = status;
        appointmentsOnDate[appointmentIndex].pagamento = paymentInfo;
        await updateDoc(appointmentRef, { [appointmentDate]: appointmentsOnDate });
      }
    }
  };
  const deleteAppointment = async (userId, appointmentDate, appointmentTxid) => {
    const userAppointmentsPath = `agendamentos/${userId}`;
    const appointmentRef = doc(db, userAppointmentsPath);
    const docSnap = await getDoc(appointmentRef);
    if (docSnap.exists()) {
      let userData = docSnap.data();
      let appointmentsOnDate = userData[appointmentDate] || [];
      appointmentsOnDate = appointmentsOnDate.filter(appointment => appointment.txid !== appointmentTxid);
      if (appointmentsOnDate.length > 0) {
        await updateDoc(appointmentRef, { [appointmentDate]: appointmentsOnDate });
      } else {
        // Se não houver mais agendamentos nessa data, remova o campo inteiro
        await updateDoc(appointmentRef, { [appointmentDate]: deleteField() });
      }
    }
  };
  useEffect(() => {
    const fetchBusinessHours = async () => {
      const querySnapshot = await getDocs(collection(db, "businessHours"));
      const hoursData = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data(); // assumindo que cada documento tem campos 'day', 'open', e 'close'
        hoursData[data.day] = { open: data.open, close: data.close, closed: data.closed };
      });
      setBusinessHours(hoursData);
    };
    fetchBusinessHours();
  }, []);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (user) {
        // Buscar informações adicionais do usuário na coleção 'users'
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          // Usuário encontrado na coleção 'users'
          setLoggedInUser({ name: userSnap.data().name, id: user.uid });
        } else {
          // Documento não existe na coleção 'users'
          console.error("Usuário não encontrado na coleção 'users'");
        }
      } else {
        // Usuário não está logado
        setLoggedInUser(null);
      }
    });
    // Limpar subscription
    return () => unsubscribe();
  }, []);

  // Busca só os documentos que têm agendamentos na data escolhida (muito mais leve
  // que baixar a coleção inteira). Se a consulta falhar, volta ao modo antigo.
  const fetchAppointmentDocsForDate = async (date) => {
    const appointmentsRef = collection(db, 'agendamentos');
    try {
      const snap = await getDocs(query(appointmentsRef, orderBy(new FieldPath(date))));
      return snap.docs;
    } catch (error) {
      console.error('Consulta por data falhou, usando busca completa:', error);
      const snap = await getDocs(appointmentsRef);
      return snap.docs;
    }
  };

  // Função para verificar se o horário já está reservado, ignorando o agendamento em edição
  const isTimeSlotAvailable = async (date, time, professional, options = {}) => {
    const { ignoreUserId, ignoreOriginalDate, ignoreOriginalHora, ignoreTxid, ignoreEndToEndId } = options;

    const appointmentDocsList = await fetchAppointmentDocsForDate(date);

    for (const docSnap of appointmentDocsList) {
      const userAppointments = docSnap.data()[date] || [];
      for (const appointment of userAppointments) {
        // Ignora o agendamento original do próprio usuário durante a edição
        const isSameAppointment =
          ignoreUserId === docSnap.id &&
          ignoreOriginalDate === date &&
          ignoreOriginalHora === appointment.hora;

        const isSameTx = ignoreTxid && appointment.txid === ignoreTxid;
        const isSameEndToEnd = ignoreEndToEndId && appointment.endToEndId === ignoreEndToEndId;

        if (isSameAppointment || isSameTx || isSameEndToEnd) {
          continue;
        }

        if (appointment.hora === time && appointment.professional === professional) {
          return false;
        }
      }
    }

    return true; // Retorna true se o horário estiver disponível
  };

  //remove os horarios ja agendados
//remove os horarios ja agendados
  const fetchAppointmentsForDate = async (date, professional) => {
    const schedule = await getScheduleForDate(date, professional);

    if (!schedule.isOpen) {
      setAvailableTimes([]);
      return;
    }

    const { hours, reservedTimes } = schedule;

    const appointmentDocsList = await fetchAppointmentDocsForDate(date);
    const bookedTimes = new Set();
    const shouldFilterByProfessional = Boolean(professional);
    appointmentDocsList.forEach((doc) => {
      const userAppointments = doc.data()[date] || [];
      userAppointments.forEach((appointment) => {
        if (!shouldFilterByProfessional || appointment.professional === professional) {
          bookedTimes.add(appointment.hora);
        }
      });
    });

    const newAvailableTimes = [];
    for (let hour = parseInt(hours.open); hour <= parseInt(hours.close); hour++) {
      const formattedHour = `${hour < 10 ? '0' : ''}${hour}:00`;
      const isReserved = reservedTimes.includes(formattedHour);
      if (!bookedTimes.has(formattedHour) && !isReserved && formattedHour !== "13:00") {
        newAvailableTimes.push(formattedHour);
      }
    }

    setAvailableTimes(newAvailableTimes);
  };

  const handleEditClick = async (appointment, appointmentGroupDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [y, m, d] = appointmentGroupDate.split('-').map(Number);
    const appointmentDate = new Date(y, m - 1, d);
    const diffTime = appointmentDate.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 3600 * 24);
    if (diffDays <= 0) {
      alert("Não é possível editar agendamentos do mesmo dia. Por favor, só agendamento com pelo menos um dia de antecedência.");
      return;
    }
    setEditingAppointment({
      ...appointment,
      originalAppointment: appointment,
      originalDate: appointment.data,
      originalHora: appointment.hora,
      date: appointmentGroupDate
    });
    await fetchAppointmentsForDate(appointmentGroupDate, appointment.professional);
    setShowModal(true);
  };
  const handleModalClose = () => {
    setShowModal(false);
    setEditingAppointment({});
  };

  const handleDateChange = async (e) => {
    const newDate = e.target.value;

    const schedule = await getScheduleForDate(newDate, editingAppointment.professional);

    if (!schedule.isOpen) {
      alert(schedule.message || "Não há atendimento neste dia. Por favor, escolha outro dia.");
      return;
    }

    setEditingAppointment(prev => ({ ...prev, date: newDate }));
    await fetchAppointmentsForDate(newDate, editingAppointment.professional);
  };

  const handleTimeChange = (e) => {
    setEditingAppointment(prev => ({ ...prev, hora: e.target.value }));
  };
  const handleUpdateAppointment = async () => {
    if (!editingAppointment.date || !editingAppointment.hora || !loggedInUser || !loggedInUser.id) {
      alert("Informações de agendamento incompletas para a atualização.");
      return;
    }

    // 1) Validar dia permitido (businessHours ou specialDay)
    const schedule = await getScheduleForDate(editingAppointment.date, editingAppointment.professional);
    if (!schedule.isOpen) {
      alert(schedule.message || "Dia indisponível para agendamento.");
      return;
    }

    // 2) Validar horário permitido (inclusive horários reservados)
    const { hours, reservedTimes } = schedule;
    const selectedHourNumber = parseInt(editingAppointment.hora);
    const isWithinHours =
      selectedHourNumber >= parseInt(hours.open) &&
      selectedHourNumber <= parseInt(hours.close);

    if (!isWithinHours) {
      alert("Horário fora do intervalo permitido para esta data.");
      return;
    }

    if (reservedTimes.includes(editingAppointment.hora)) {
      alert("Horário reservado. Por favor, escolha outro horário.");
      return;
    }

    // 3) Validar disponibilidade real, ignorando o próprio agendamento
    const isAvailable = await isTimeSlotAvailable(
      editingAppointment.date,
      editingAppointment.hora,
      editingAppointment.professional,
      {
        ignoreUserId: loggedInUser.id,
        ignoreOriginalDate: editingAppointment.originalDate,
        ignoreOriginalHora: editingAppointment.originalHora,
        ignoreTxid: editingAppointment.txid,
        ignoreEndToEndId: editingAppointment.endToEndId,
      }
    );

    if (!isAvailable) {
      alert("Este horário não está disponível. Por favor, escolha outro horário.");
      return;
    }

    try {
      const appointmentPath = `agendamentos/${loggedInUser.id}`;
      const appointmentRef = doc(db, appointmentPath);  
      const docSnapshot = await getDoc(appointmentRef);
      if (!docSnapshot.exists()) {
        console.error('Documento de agendamentos não encontrado.');
        return;
      }

      let appointmentsData = docSnapshot.data();
      const originalDate = editingAppointment.originalDate;
      const newAppointmentDate = editingAppointment.date;

      // 4) Remover o agendamento antigo sempre
      if (appointmentsData[originalDate]) {
        appointmentsData[originalDate] = appointmentsData[originalDate].filter(appointment =>
          !(appointment.hora === editingAppointment.originalHora && appointment.professional === editingAppointment.professional)
        );

        if (appointmentsData[originalDate].length === 0) {
          appointmentsData[originalDate] = deleteField();
        }
      }

      // 5) Criar apenas um agendamento novo e evitar duplicações
      if (!appointmentsData[newAppointmentDate] || appointmentsData[newAppointmentDate] === deleteField()) {
        appointmentsData[newAppointmentDate] = [];
      }

      // Remover qualquer agendamento duplicado que possa existir na data selecionada
      appointmentsData[newAppointmentDate] = appointmentsData[newAppointmentDate].filter(appointment =>
        !(appointment.professional === editingAppointment.professional && appointment.hora === editingAppointment.hora)
      );

      appointmentsData[newAppointmentDate].push({
        ...editingAppointment,
        data: newAppointmentDate,
      });

      await updateDoc(appointmentRef, appointmentsData);

      // Limpa duplicidades do próprio usuário como reforço de segurança
      await cleanupDuplicateAppointments(loggedInUser.id);

      alert('Agendamento atualizado com sucesso!');
      setShowModal(false);
      setEditingAppointment({});
  
      await fetchUserAppointments(loggedInUser.id);
    } catch (error) {
      console.error('Erro ao editar agendamento:', error);
      alert(`Erro ao editar agendamento. Detalhes: ${error.message || error}`);
    }
  };
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        // Verifica pagamentos pendentes e duplicidades SÓ do próprio usuário,
        // antes de listar (para a lista já refletir o resultado)
        await verifyPaymentsForPendingAppointments(user.uid);
        await cleanupDuplicateAppointments(user.uid);
        fetchUserAppointments(user.uid);
      } else {
        setAppointments([]); // Limpar agendamentos se não houver usuário logado
        setLoading(false);
      }
    });
    return () => unsubscribe(); // Limpar subscription
  }, []);
  const fetchUserAppointments = async (userId) => {
    try {
      const userAppointmentsPath = `agendamentos/${userId}`;
      const userAppointmentsRef = doc(db, userAppointmentsPath);
      const docSnap = await getDoc(userAppointmentsRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        let userAppointments = Object.keys(userData).map((date) => {
          // Ordena os agendamentos por hora para cada data
          const appointmentsSortedByTime = userData[date].sort((a, b) => a.hora.localeCompare(b.hora));
          return { date, appointments: appointmentsSortedByTime };
        });
        // Ordenar os agendamentos por data em ordem ascendente
        userAppointments.sort((a, b) => new Date(a.date) - new Date(b.date));
        // Filtrar os agendamentos para incluir somente os que estão pelo menos 3 dias à frente
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Ajusta para o início do dia
        const threeDaysLater = new Date(today);
        threeDaysLater.setDate(today.getDate() - 3); // Adiciona 3 dias à data atual
        userAppointments = userAppointments.filter(appointmentGroup =>
          new Date(appointmentGroup.date) >= threeDaysLater
        );
        setAppointments(userAppointments);
      } else {
        console.log("Nenhum agendamento encontrado.");
      }
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
    }
    setLoading(false);
  };

  // Recupera as regras do dia considerando horários padrão e especiais
  const getScheduleForDate = async (date, professional) => {
    const dayOfWeek = new Date(date).getDay();
    const specialDayRef = doc(db, "specialDays", date);
    const specialDaySnap = await getDoc(specialDayRef);

    const defaultHours = businessHours[dayOfWeek];

    // Se não existir horário padrão e não houver dia especial, o dia é inválido
    if (!defaultHours && !specialDaySnap.exists()) {
      return { isOpen: false, message: "Dia sem horário cadastrado." };
    }

    if (specialDaySnap.exists()) {
      const specialDayData = specialDaySnap.data();
      const professionalDetails = professional && specialDayData.professionals
        ? specialDayData.professionals[professional]
        : undefined;

      const mergedDetails = {
        status: professionalDetails?.status ?? specialDayData.status,
        open: professionalDetails?.open ?? specialDayData.open,
        close: professionalDetails?.close ?? specialDayData.close,
        times: professionalDetails?.times ?? specialDayData.times,
      };

      if (mergedDetails.status === "Fechado") {
        if (mergedDetails.open && mergedDetails.close) {
          const blockedTimes = [];
          for (let h = parseInt(mergedDetails.open); h <= parseInt(mergedDetails.close); h++) {
            blockedTimes.push(`${h.toString().padStart(2, '0')}:00`);
          }
          return { isOpen: true, hours: defaultHours, reservedTimes: blockedTimes };
        }
        return { isOpen: false, message: "Dia fechado." };
      }

      if (mergedDetails.status === "Especial" && mergedDetails.open && mergedDetails.close) {
        return {
          isOpen: true,
          hours: { open: mergedDetails.open, close: mergedDetails.close },
          reservedTimes: Array.isArray(mergedDetails.times)
            ? mergedDetails.times.map(time => time.close || time).filter(Boolean)
            : [],
        };
      }

      if (mergedDetails.status === "Reservado") {
        const reservedHours = mergedDetails.open && mergedDetails.close
          ? { open: mergedDetails.open, close: mergedDetails.close }
          : defaultHours;

        if (!reservedHours) {
          return { isOpen: false, message: "Horário não configurado para o dia reservado." };
        }

        return {
          isOpen: true,
          hours: reservedHours,
          reservedTimes: Array.isArray(mergedDetails.times)
            ? mergedDetails.times.map(time => time.close || time).filter(Boolean)
            : [],
        };
      }
    }

    if (!defaultHours) {
      return { isOpen: false, message: "Horário padrão não encontrado para este dia." };
    }

    if (defaultHours.closed) {
      return { isOpen: false, message: "Dia fechado para agendamentos." };
    }

    return { isOpen: true, hours: defaultHours, reservedTimes: [] };
  };


  const handleCancelClick = () => {
    navigate('/homeCli');  
  };
  return (
    <div className="agendamentos-container">
      <nav className="navbar">
        <img src={LogoBarberia} alt="Logo" className="logo" />
        <div className="navbar-center">
          <div className="navbar-links">
            <h3>Meus agendamentos</h3>
          </div>
        </div>
        <button className="logout-button" onClick={handleCancelClick}>Voltar</button>
      </nav>
      {loading ? <p className="loading-text">Carregando...</p> : (
        <table className="appointments-table">
          <thead>
            <tr>
              <th>Profissional</th>
              <th>Serviço</th>
              <th>Produto</th>
              <th>Data</th>
              <th>Horário</th>
              <th>Pagamento</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appointmentGroup, index) => (
              appointmentGroup.appointments.map((appointment, subIndex) => (
                <tr key={subIndex}>
                  <td>{appointment.professional}</td>
                  <td>{appointment.services}</td>
                  <td>{appointment.products}</td>
                  <td>{appointmentGroup.date}</td>
                  <td>{appointment.hora}</td>
                  <td>{appointment.paymentStatus}</td>
                  <td>
                    <button onClick={() => handleEditClick(appointment, appointmentGroup.date)}>Editar</button>
                  </td>
                </tr>
              ))
            ))}
          </tbody>
        </table>
      )}
      {showModal && (
        <div className="edit-modal-overlay">
          <div className="edit-modal-container">
            <span className="edit-modal-close" onClick={handleModalClose}>&times;</span>
            <h2 className="edit-modal-title">Editar Agendamento</h2>
            <label className="edit-modal-label">Data:</label>
            <input className="edit-modal-input" type="date" min={new Date(Date.now() + 86400000).toISOString().split('T')[0]} value={editingAppointment.date} onChange={handleDateChange} />
            <label className="edit-modal-label">Horário:</label>
            <select className="edit-modal-input" value={editingAppointment.hora} onChange={handleTimeChange}>
              {availableTimes.map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
            <button className="edit-modal-button" onClick={handleUpdateAppointment}>Salvar Alterações</button>
          </div>
        </div>
      )}
    </div>
  );
}